export interface TimetableLesson {
  id: number
  slot: number
  journalId: number | null
  journalName: string
  topic: string
  color: string
  time: string
  locked: boolean
}

export interface Journal {
  id: number
  name: string
  color: string
  students: number
  locked: boolean
}

export interface UpcomingEvent {
  id: number
  type: 'school_event' | 'teacher_absence'
  title: string
  when: string
  description?: string
  userLetters?: string
}

export interface WhiteboardItem {
  id: number
  title: string
  body: string
  visibility: 'private' | 'staff'
  hasAttachment: boolean
}

export interface LessonData {
  id: number
  slot: number
  journalId: number | null
  journalName: string
  topic: string
  color: string
  time: string
  locked: boolean
  eventDateLabel: string
  lessonType: string
  teacher: string
  teacherInitials: string
  created: string
  descriptionHtml: string
}

export const TIMETABLE_LESSONS: TimetableLesson[] = [
  {
    id: 1,
    slot: 1,
    journalId: 1,
    journalName: '9B English',
    topic: 'Reading test + word formation',
    color: '#0274EF',
    time: '08:00 – 08:40',
    locked: false,
  },
  {
    id: 2,
    slot: 2,
    journalId: 2,
    journalName: '10A Mathematics',
    topic: 'Quadratic equations practice',
    color: '#7C3AED',
    time: '08:50 – 09:30',
    locked: false,
  },
  {
    id: 3,
    slot: 3,
    journalId: 3,
    journalName: '8C History',
    topic: 'World War II overview',
    color: '#D9A602',
    time: '09:50 – 10:35',
    locked: true,
  },
  {
    id: 4,
    slot: 4,
    journalId: null,
    journalName: '',
    topic: '',
    color: '',
    time: '10:45 – 11:30',
    locked: false,
  },
  {
    id: 5,
    slot: 5,
    journalId: 1,
    journalName: '9B English',
    topic: 'Pair work exercise',
    color: '#0274EF',
    time: '11:20 – 12:00',
    locked: false,
  },
  {
    id: 6,
    slot: 6,
    journalId: null,
    journalName: '',
    topic: '',
    color: '',
    time: '12:45 – 13:30',
    locked: false,
  },
  {
    id: 7,
    slot: 7,
    journalId: null,
    journalName: '',
    topic: '',
    color: '',
    time: '13:40 – 14:25',
    locked: false,
  },
  {
    id: 8,
    slot: 8,
    journalId: null,
    journalName: '',
    topic: '',
    color: '',
    time: '14:35 – 15:20',
    locked: false,
  },
]

export const JOURNALS: Journal[] = [
  { id: 1, name: 'English 9B', color: '#0274EF', students: 28, locked: false },
  { id: 2, name: 'Mathematics 10A', color: '#7C3AED', students: 24, locked: false },
  { id: 3, name: 'History 8C', color: '#D9A602', students: 26, locked: true },
  { id: 4, name: 'Biology 9B', color: '#16A34A', students: 22, locked: false },
  { id: 5, name: 'Physics 11A', color: '#0EA5E9', students: 20, locked: false },
  { id: 6, name: 'Geography 7A', color: '#EA580C', students: 30, locked: false },
  { id: 7, name: 'English 10A', color: '#0274EF', students: 26, locked: false },
  { id: 8, name: 'English 8A', color: '#7C3AED', students: 25, locked: false },
  { id: 9, name: 'Mathematics 9A', color: '#D946EF', students: 27, locked: false },
  { id: 10, name: 'Chemistry 11B', color: '#16A34A', students: 18, locked: false },
  { id: 11, name: 'Estonian 7B', color: '#F59E0B', students: 29, locked: false },
  { id: 12, name: 'Art 5A', color: '#F43F5E', students: 32, locked: false },
  { id: 13, name: 'Mathematics 11A', color: '#6B7280', students: 21, locked: false },
]

export const INITIAL_EVENTS: UpcomingEvent[] = [
  {
    id: 1,
    type: 'school_event',
    title: 'Parent evening',
    when: 'Thu 27 Mar, 17:00 – 19:00',
    description: 'Grades review and Q&A with parents',
  },
  {
    id: 2,
    type: 'school_event',
    title: 'Staff meeting',
    when: 'Fri 28 Mar, 14:00 – 15:00',
    description: 'End of term planning session',
  },
  {
    id: 3,
    type: 'school_event',
    title: 'School assembly',
    when: 'Mon 31 Mar, 09:00 – 09:30',
    description: 'Quarterly awards ceremony',
  },
]

export const INITIAL_WHITEBOARD: WhiteboardItem[] = [
  {
    id: 1,
    title: 'Test next Tuesday',
    body: 'Unit 4 vocabulary and reading comprehension',
    visibility: 'staff',
    hasAttachment: false,
  },
  {
    id: 2,
    title: 'Bring lab coats',
    body: 'Required for chemistry experiments this week',
    visibility: 'private',
    hasAttachment: true,
  },
  {
    id: 3,
    title: 'Field trip permission',
    body: 'Collect signed forms by Friday',
    visibility: 'staff',
    hasAttachment: true,
  },
]

export const LESSON_SLOT_LABELS: Record<number, string> = {
  1: '08:00–08:45',
  2: '08:55–09:40',
  3: '09:50–10:35',
  4: '10:45–11:30',
  5: '11:50–12:35',
  6: '12:45–13:30',
  7: '13:40–14:25',
  8: '14:35–15:20',
}

export const LESSON_TYPES = [
  { value: 'regular', label: 'Regular lesson' },
  { value: 'lab', label: 'Laboratory' },
  { value: 'excursion', label: 'Excursion' },
]

export const ABSENCE_REASONS = [
  { value: 'sick', label: 'Sick leave' },
  { value: 'training', label: 'Professional development' },
  { value: 'personal', label: 'Personal' },
  { value: 'other', label: 'Other' },
]
